import { Box, Divider, Flex } from "styled-system/jsx";
import { SectionHeading } from "../SectionHeading/SectionHeading";
import { useTranslation } from "react-i18next";
import { EventManyQuery, EventWithOccurrences } from "gql/generated";
import { Button, Icon, Spinner, Tag, Text } from "@ttc3k/trekker";
import { EmptyState } from "shared/components";
import { useRecurringEvents } from "./useRecurringEvents";
import {
  getFormattedEventDetailsLink,
  getMultilingualString,
  getRecurrencePatternDescription,
} from "shared/utils";
import { useEventsWidgetContext } from "../EventsContext";
import { recurringEventsRecipe } from "./recipe";
import { EventWidgetSize } from "widgets/events/types";
import { ArrowDown, ArrowRight } from "iconoir-react";
import { useScroll } from "shared/hooks";
import { PostHogCustomEvent, usePostHog } from "shared/hooks/usePostHog";

type RecurringEventsProps = {
  events: EventManyQuery["eventMany"][0][];
  loading: boolean;
};

export const RecurringEvents = ({ events, loading }: RecurringEventsProps) => {
  const { t } = useTranslation("events", { keyPrefix: "WIDGET" });
  const {
    eventDetailsLink,
    eventDetailsDisplayMode,
    locale,
    widgetSize,
    setSelectedEvent,
  } = useEventsWidgetContext();
  const { eventGroups } = useRecurringEvents(events);
  const { organizationId, widgetId } = useEventsWidgetContext();
  const { trackPostHogEvent } = usePostHog();

  const classes = recurringEventsRecipe({ size: widgetSize });
  const { scrollRef, atEnd, isOverflowing } = useScroll({
    mode: "vertical",
    postHogEvent: {
      leftOrUp: PostHogCustomEvent.ScrollRecurringEventsUp,
      rightOrDown: PostHogCustomEvent.ScrollRecurringEventsDown,
    },
  });

  // If we're in link mode, we open the event details in a new tab following the provided link format
  const handleEventDetailsLinkClick = (event: EventWithOccurrences) => {
    if (eventDetailsLink && eventDetailsDisplayMode === "link") {
      trackPostHogEvent(PostHogCustomEvent.ExpandedDetailsClick, {
        organizationId: organizationId,
        widgetId: widgetId,
        eventId: event._id,
      });
      window.open(
        getFormattedEventDetailsLink(event, eventDetailsLink!),
        "_blank",
      );
    } else {
      trackPostHogEvent(PostHogCustomEvent.DetailsClick, {
        organizationId: organizationId,
        widgetId: widgetId,
        eventId: event._id,
      });
      setSelectedEvent(event);
    }
  };

  /**
   * The Lenis library that is used on the Payload sites blocks
   * default wheel events on scroll containers that are within the
   * main page's scroll container. This workaround listens for wheel
   * events (because those are not blocked) and manually scrolls the
   * scroll container instead.
   */
  /* Commented out because Lenis ALSO captures the scroll event,
  /* so this area scrolls, but so does the main page.
  useEffect(() => {
    const div = scrollRef.current;
    if (!div) return;

    const handleWheel = (event: WheelEvent) => {
      const nextScrollPosition = div.scrollTop + event.deltaY;
      const scrollTopMax = div.scrollHeight - div.clientHeight;

      // If the next scroll position is within the bounds,
      // attempt to stop the default behaviour (not sure if this will work with Lenis)
      if (nextScrollPosition >= 0 && nextScrollPosition <= scrollTopMax) {
        event.preventDefault();
      }

      const nextScrollTop = Math.max(
        0,
        Math.min(nextScrollPosition, div.scrollHeight - div.clientHeight),
      );
      div.scrollTop = nextScrollTop;
    };

    div.addEventListener("wheel", handleWheel, { passive: false });

    const scrollForMore = scrollRef.current?.parentElement?.querySelector(
      '[part="recurring__container-scroll-for-more"]',
    ) as HTMLElement;
    if (scrollForMore) {
      scrollForMore.addEventListener("wheel", handleWheel, { passive: false });
    }

    return () => {
      div.removeEventListener("wheel", handleWheel);
      scrollForMore?.removeEventListener("wheel", handleWheel);
    };
    // Note: The React warning here can be ignored
    // Using scrollRef.current actually works as expected because it changes from null to a value
  }, [scrollRef, scrollRef.current]);
  */

  return (
    <Flex part="events__container-recurring" flexDir="column" gap="400">
      <SectionHeading heading={t("LABELS.RECURRING_EVENTS")} />
      <Box className={classes.mainContainer}>
        {loading ? (
          <Flex
            w={"full"}
            h={"full"}
            justifyContent={"center"}
            alignItems={"center"}
          >
            <Spinner width={"40px"} height={"40px"} />
          </Flex>
        ) : (
          <>
            {/*
            When the EmptyState and scrollRef were dynamically swapped, the scrollRef would become null
            and the scroll watcher would stop working correctly. This happened when going from a month
            with recurring events to a month without them. Moving the EmptyState into the scrollRef ensures
            it does not become null
            */}
            <Flex
              part="recurring__container-scroller"
              ref={scrollRef}
              className={classes.scrollContainer}
            >
              {eventGroups.length === 0 && (
                <EmptyState
                  part="events__recurring-no-data"
                  text={t("NO_DATA.RECURRING")}
                  className={classes.emptyState}
                />
              )}
              {eventGroups.map(group => (
                <Flex
                  part="recurring__container-date-group"
                  key={group.startDate}
                  className={classes.eventGroups}
                >
                  <Flex flexDir="column" gap="150">
                    <Tag part={"card__tag"} visual="primary" mode="light">
                      {group.formattedDate}
                    </Tag>
                    {group.events.map(event => (
                      <Flex
                        part="recurring__container-event"
                        key={event._id}
                        flexDir="row"
                        alignItems="center"
                        gap="200"
                        w="full"
                      >
                        <Flex flexDir="column" gap="50" flexGrow="1">
                          <Text
                            part="recurring__title"
                            visual="bodySemiBold"
                            lineClamp="2"
                          >
                            {getMultilingualString(event.title)}
                          </Text>
                          <Text
                            part="recurring__venue"
                            visual="smallSemiBold"
                            color="text.mid"
                            lineClamp="1"
                          >
                            {getMultilingualString(event.eventLocation.venue)}
                          </Text>
                          <Text
                            part="recurring__pattern"
                            visual="smallTag"
                            color="text.light"
                            lineClamp="1"
                          >
                            {getRecurrencePatternDescription(t, locale, event)}
                          </Text>
                        </Flex>
                        <Button
                          part="recurring__btn-details"
                          flexShrink="0"
                          Icon={ArrowRight}
                          visual="primary"
                          mode="light"
                          borderRadius="full"
                          onClick={() =>
                            handleEventDetailsLinkClick(
                              event as EventWithOccurrences,
                            )
                          }
                          w="32px"
                          h="32px"
                          boxShadow="0px 1px 4px 0px rgba(10, 10, 10, 0.20), 0px 1px 2px 0px rgba(10, 10, 10, 0.10)"
                          mr="100"
                        />
                      </Flex>
                    ))}
                  </Flex>
                </Flex>
              ))}
            </Flex>
            {widgetSize !== EventWidgetSize.Mobile && (
              <Flex
                part="recurring__container-scroll-for-more"
                className={classes.scrollForMore}
                data-has-overflow={isOverflowing}
                data-at-end={atEnd}
                tabIndex={0}
                onKeyDown={e => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    scrollRef.current?.scrollTo({
                      top: scrollRef.current?.scrollHeight,
                      behavior: "smooth",
                    });
                  }
                }}
                onClick={() => {
                  scrollRef.current?.scrollTo({
                    top: scrollRef.current?.scrollHeight,
                    behavior: "smooth",
                  });
                }}
                cursor="pointer"
                outline="none"
              >
                <Divider
                  flexGrow="1"
                  borderColor="border.mid"
                  borderStyle="dashed"
                />
                <Flex alignItems="center" gap="75" flexGrow="1">
                  <Text color="text.mid" whiteSpace="nowrap">
                    {t("WORDS.SCROLL_FOR_MORE")}
                  </Text>
                  <Icon
                    Element={ArrowDown}
                    color="icon.mid"
                    w="20px"
                    h="20px"
                  />
                </Flex>
                <Divider
                  flexGrow="1"
                  borderColor="border.mid"
                  borderStyle="dashed"
                />
              </Flex>
            )}
          </>
        )}
      </Box>
    </Flex>
  );
};
